import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { from, Observable, Subject } from 'rxjs';
import { environment } from 'oneok/environments/environment';
import { Account as AccountService } from '@customer/account/account.svc';
import { SpinnerService, StorageService, StrapiService, ToastService } from '@core/services';
import { SignoutResponse, User, UserManager } from 'oidc-client';
import * as CryptoJS from 'crypto-js';
import posthog from 'posthog-js';

@Injectable({
  providedIn: 'root'
})
export class Auth {
  baseUrl = environment.apiUrlV2;
  testAcct: unknown;
  private readonly _userManager: UserManager;
  private readonly _loginChangedSubject = new Subject<boolean>();
  testVersion: string | undefined;
  tParse = '93243b0e-6fbf-4a68-a6c1-6da4b4e3c3e4fZK96ujnFfZJJRWu3zT/LQ==';
  loginChanged$ = this._loginChangedSubject.asObservable();
  private readonly _loginUserSubject = new Subject<unknown>();
  loginUser$ = this._loginUserSubject.asObservable();
  cusUserRegex = /^CUS\d{5}$/i;
  private readonly _secureAuthUserSource = new Subject<User | null>();
  readonly secureAuthUser$ = this._secureAuthUserSource.asObservable();
  private readonly _router = inject(Router);
  private readonly _http = inject(HttpClient);
  private readonly _accountSvc = inject(AccountService);
  readonly spinnerSvc = inject(SpinnerService);
  readonly storageSvc = inject(StorageService);
  private readonly _strapiSvc = inject(StrapiService);
  private readonly _toastSvc = inject(ToastService);

  constructor() {
    const stsSettings = {
      authority: this.dTest(`${environment.secureAuthInternal.authority}`),
      client_id: this.dTest(`${environment.secureAuthInternal.client_id}`),
      response_type: `${environment.secureAuthInternal.response_type}`,
      scope: `${environment.secureAuthInternal.scope}`,
      redirect_uri: `${environment.secureAuthInternal.redirect_uri}`,
      post_logout_redirect_uri: `${environment.secureAuthInternal.redirect_uri}`,
      loadUserInfo: true,
      automaticSilentRenew: false,
      monitorSession: false
    };
    this._userManager = new UserManager(stsSettings);
  }

  dTest(string: string): string {
    return CryptoJS.AES.decrypt(string, this.tParse).toString(CryptoJS.enc.Utf8);
  }

  setSecureAuthUser(val: User | null): void {
    this._secureAuthUserSource.next(val ?? null);
  }

  getAuthToken(): string | null {
    const jwt = sessionStorage?.getItem('authToken');
    return jwt;
  }

  //removes auth session info
  clearCurrentUser(): void {
    this.storageSvc.clearCurrentUser();
  }

  setAuthCookie(token: string, email: string): string {
    const tokenStr = token.replace(/"/g, '');
    const b64Token = btoa(email + ':' + tokenStr);
    return b64Token;
  }

  logout(): void {
    this.storageSvc.clearAppStorage();
    this._router.navigate(['/login']);
    this._secureAuthUserSource.next(null);
  }

  loginUsers(): Promise<void> {
    return this._userManager.signinRedirect();
  }

  completeInternalUsersLogin(): Observable<User | void> {
    return from(
      this._userManager
        .signinRedirectCallback()
        .then()
        .catch(err => {
          /* empty */
        })
    );
  }

  logoutUser(): Promise<SignoutResponse> {
    this._toastSvc.dismissAll();
    this.storageSvc.clearAppStorage();
    window.sessionStorage.clear();
    this._router.navigate(['/login']);
    this._secureAuthUserSource.next(null);
    posthog.reset();
    return this._userManager.signoutRedirectCallback();
  }

  isCusUser(userName: string): boolean {
    return this.cusUserRegex.test(userName);
  }

  eTest(string: string): string {
    return CryptoJS.AES.encrypt(string, this.tParse).toString();
  }
}
